import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';

const BALL_LEVELS = [
  { value: 'red', label: 'Red Ball', description: 'Ages 5–8' },
  { value: 'orange', label: 'Orange Ball', description: 'Ages 9–10' },
  { value: 'green', label: 'Green Ball', description: 'Ages 11–12' },
  { value: 'yellow', label: 'Yellow Ball', description: 'Ages 12+' },
] as const;

const SESSION_TYPES = [
  { value: 'group',         label: 'Group',         description: 'Group coaching' },
  { value: 'private',       label: 'Private',       description: 'One-on-one lessons' },
  { value: 'clinic',        label: 'Clinic',        description: 'Specialised clinics' },
  { value: 'match_play',    label: 'Match Play',    description: 'Competitive match practice' },
  { value: 'cardio_tennis', label: 'Cardio Tennis', description: 'Fitness-focused sessions' },
  { value: 'squads',        label: 'Squads',        description: 'Squad/team training' },
] as const;

type SessionTypeValue = typeof SESSION_TYPES[number]['value'];

export default function ParentOnboardPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [code, setCode] = useState(searchParams.get('code') ?? '');
  const [debouncedCode, setDebouncedCode] = useState(code);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [ballLevel, setBallLevel] = useState<'red' | 'orange' | 'green' | 'yellow' | ''>('');
  const [selectedSessionTypes, setSelectedSessionTypes] = useState<SessionTypeValue[]>([]);
  const [consentChecked, setConsentChecked] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedCode(code.trim().toUpperCase()), 400);
    return () => clearTimeout(t);
  }, [code]);

  const preview = useQuery(
    api.parent.claimOnboardingInvite.preview,
    debouncedCode.length === 8 ? { code: debouncedCode } : 'skip'
  );

  const claim = useMutation(api.parent.claimOnboardingInvite.claim);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!consentChecked) { setError('Please confirm parental consent to continue.'); return; }
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await claim({
        code: code.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dateOfBirth: dateOfBirth || undefined,
        ballLevel: ballLevel || undefined,
        sessionTypes: selectedSessionTypes.length > 0 ? selectedSessionTypes : undefined,
        consentGiven: true,
      });
      if (result.duplicateWarning) {
        setDuplicateWarning(true);
      }
      setSuccess(true);
      setTimeout(() => navigate(`/parent/children/${result.playerId}`), 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const isValidCode = preview?.valid === true;
  const codeError =
    debouncedCode.length === 8 && preview && !preview.valid
      ? preview.reason === 'already_claimed' ? 'This invite has already been used.'
        : preview.reason === 'expired' ? 'This invite has expired. Ask the director for a new one.'
        : 'Invite code not found. Check the code and try again.'
      : null;

  if (success) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
          <h2 className="text-xl font-bold text-foreground">Welcome aboard!</h2>
          {duplicateWarning && (
            <p className="text-sm text-amber-600 flex items-center justify-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              A player with this name already exists — your director can merge if needed.
            </p>
          )}
          <p className="text-muted-foreground">Taking you to your child's profile…</p>
        </div>
      </div>
    );
  }

  const canSubmit = isValidCode && firstName.trim() && lastName.trim() && consentChecked;

  return (
    <div className="max-w-md mx-auto p-4 py-10 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Add your child</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Enter your invite code and your child's details to get started.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Invite code */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Invite code</label>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 8))}
            placeholder="e.g. XK7M2P9Q"
            className="font-mono text-lg tracking-widest uppercase"
            maxLength={8}
            required
          />
          {codeError && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {codeError}
            </p>
          )}
          {isValidCode && (
            <p className="text-sm text-green-700 flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              Joining <strong className="ml-1">{preview.orgName}</strong>
            </p>
          )}
        </div>

        {/* Child details — only shown once code is valid */}
        {isValidCode && (
          <>
            <div className="border-t pt-5 space-y-4">
              <p className="text-sm font-medium text-foreground">Your child's details</p>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">First name *</label>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Last name *</label>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Date of birth (optional)</label>
                <Input
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Ball level (optional — your director can set this)</label>
                <div className="grid grid-cols-2 gap-2">
                  {BALL_LEVELS.map((b) => (
                    <button
                      key={b.value}
                      type="button"
                      onClick={() => setBallLevel(ballLevel === b.value ? '' : b.value)}
                      className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                        ballLevel === b.value
                          ? 'border-violet-500 bg-violet-50 text-violet-800'
                          : 'border-border hover:border-primary text-foreground'
                      }`}
                    >
                      <div className="font-medium">{b.label}</div>
                      <div className="text-xs text-muted-foreground">{b.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">
                  Which programs will your child join? (optional — select all that apply)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {SESSION_TYPES.map((s) => {
                    const isSelected = selectedSessionTypes.includes(s.value);
                    return (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() =>
                          setSelectedSessionTypes((prev) =>
                            isSelected
                              ? prev.filter((t) => t !== s.value)
                              : [...prev, s.value]
                          )
                        }
                        className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                          isSelected
                            ? 'border-violet-500 bg-violet-50 text-violet-800'
                            : 'border-border hover:border-primary text-foreground'
                        }`}
                      >
                        <div className="font-medium">{s.label}</div>
                        <div className="text-xs text-muted-foreground">{s.description}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Consent */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              />
              <span className="text-sm text-muted-foreground">
                I confirm I have parental or guardian consent to create this profile and receive
                updates about this player.{' '}
                <Link to="/privacy" className="underline text-violet-600 hover:text-violet-800">
                  Privacy Policy
                </Link>
              </span>
            </label>
          </>
        )}

        {error && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> {error}
          </p>
        )}

        <Button
          type="submit"
          className="w-full bg-violet-600 hover:bg-violet-700"
          disabled={isSubmitting || !canSubmit}
        >
          {isSubmitting ? 'Creating profile…' : 'Create profile'}
        </Button>
      </form>

      <p className="text-xs text-center text-muted-foreground">
        Don't have an invite? Contact your club director.
      </p>
    </div>
  );
}
